import { PropsWithoutRef } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import { SIDEBAR_WIDTH } from "../constants";
import useAuth from "../hooks/useAuth";
import { ROLE } from "../constants/enums";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuth();

  // Extract permissions from user object
  const getPermissions = () => {
    // Support both `accessRole` and `accessRoleId` shapes
    const roleContainer: any = (user as any)?.accessRole || {};
    return roleContainer?.permission || [];
  };

  // Check if the user is an admin
  const isAdminRole = () => {
    const role = (user as unknown as { role?: number | string })?.role;
    return role === ROLE.ADMIN || role === "ADMIN";
  };

  // Check if the user can view a module
  const canView = (label: string): boolean => {
    if (isAdminRole()) return true;
    const permissions = getPermissions();
    const matched = permissions?.find((p: { label: string }) => p?.label === label);
    return !!matched?.isView;
  };

  const drawer = (
    <>
      <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate("/dashboard");
          }}
          className="logo_link"
        >
          <figure>
            <img src="/static/images/logo.svg" alt="Magrudy Logo" />
          </figure>
        </a>

        <IconButton
          sx={{
            display: "none",
            position: "absolute",
            top: "10px",
            right: "10px",
          }}
          onClick={handleSidebarToggle}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <List sx={{ flexGrow: 1 }} className="sidebr-lst">
        {canView("Dashboard") && (
          <Tooltip title="Dashboard" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/dashboard"}
              onClick={() => navigate("/dashboard")}
            >
              <ListItemIcon>
                <img src="/static/images/dashboard_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Dashboard" />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Manage Users") && (
          <Tooltip title="Manage Users" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-users" ||
                location.pathname === "/manage-users/edit" ||
                location.pathname === "/manage-users/detail"
              }
              onClick={() => navigate("/manage-users")}
            >
              <ListItemIcon>
                <img src="/static/images/user_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage Users" />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Rewards & Offers Management") && (
          <Tooltip title="Rewards & Offers Management" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-rewards" ||
                location.pathname === "/manage-rewards/add" ||
                location.pathname === "/manage-rewards/detail"
              }
              onClick={() => navigate("/manage-rewards")}
            >
              <ListItemIcon>
                <img src="/static/images/reward_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Rewards & Offers Management"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Transactions & Loyalty Logs") && (
          <Tooltip title="Transactions & Loyalty Logs" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-transactions" ||
                location.pathname === "/manage-transactions/view"
              }
              onClick={() => navigate("/manage-transactions")}
            >
              <ListItemIcon>
                <img src="/static/images/transaction_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Transactions & Loyalty Logs"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Online Brands Management") && (
          <Tooltip title="Online Brands Management" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-promotions" ||
                location.pathname === "/manage-promotions/add" ||
                location.pathname === "/manage-promotions/edit" ||
                location.pathname === "/manage-promotions/view"
              }
              onClick={() => navigate("/manage-promotions")}
            >
              <ListItemIcon>
                <img src="/static/images/offer_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Online Brands Management" />
            </ListItemButton>
          </Tooltip>
        )}

        {/* {canView("QR Code Management") && (
          <Tooltip title="QR Code Management" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/manage-qr-code"}
              onClick={() => navigate("/manage-qr-code")}
            >
              <ListItemIcon>
                <img src="/static/images/qr_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="QR Code Management"
              />
            </ListItemButton>
          </Tooltip>
        )} */}

        {canView("Store Management") && (
          <Tooltip title="Store Management" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-stores" ||
                location.pathname === "/manage-stores/add" ||
                location.pathname === "/manage-stores/detail"
              }
              onClick={() => navigate("/manage-stores")}
            >
              <ListItemIcon>
                <img src="/static/images/store_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Store Management" />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Feedback, Reports & Logs") && (
          <Tooltip title="Feedback, Reports & Logs" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/manage-feedback-and-reports"}
              onClick={() => navigate("/manage-feedback-and-reports")}
            >
              <ListItemIcon>
                <img src="/static/images/report_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Feedback, Reports & Logs" />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("System Settings") && (
          <Tooltip title="System Settings" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-email-template" ||
                location.pathname === "/manage-email-template/add-email-template"
              }
              onClick={() => navigate("/manage-email-template")}
            >
              <ListItemIcon>
                <img src="/static/images/settings.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Email Template"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Notification Center") && (
          <Tooltip title="Notification Center" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-notifications" ||
                location.pathname === "/manage-notifications/add" ||
                location.pathname === "/manage-notifications/detail"
              }
              onClick={() => navigate("/manage-notifications")}
            >
              <ListItemIcon>
                <img src="/static/images/notification2_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Notification Center"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Activity Logs") && (
          <Tooltip title="Activity Logs" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/manage-activity-logs"}
              onClick={() => navigate("/manage-activity-logs")}
            >
              <ListItemIcon>
                <img src="/static/images/logs_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Activity Logs"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Manage Sub-Admin") && (
          <Tooltip title="Manage Sub-Admin" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-subadmin" ||
                location.pathname === "/manage-subadmin/add" ||
                location.pathname === "/manage-subadmin/detail" ||
                location.pathname === "/manage-subadmin/roles" ||
                location.pathname === "/manage-subadmin/roles/add" ||
                location.pathname === "/manage-subadmin/roles/detail"
              }
              onClick={() => navigate("/manage-subadmin")}
            >
              <ListItemIcon>
                <img src="/static/images/user_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage Sub-Admin" />
            </ListItemButton>
          </Tooltip>
        )}

        {canView("Manage CMS Pages") && (
          <Tooltip title="Manage CMS Pages" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/manage-cms"}
              onClick={() => navigate("/manage-cms")}
            >
              <ListItemIcon>
                <img src="/static/images/cms_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage CMS Pages" />
            </ListItemButton>
          </Tooltip>
        )}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: mobileOpen ? SIDEBAR_WIDTH : 0,
        transition: "width 0.3s",
        overflowX: "hidden",
      }}
      className={mobileOpen ? "sidebar-main-div active" : "sidebar-main-div"}
    >
      <Drawer
        className="sidebar_drawer"
        variant="persistent"
        sx={{
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}